import {
  acquireExclusiveCustomerDisplayLock,
  CUSTOMER_DISPLAY_LOCK_STATUS,
} from './customerDisplayLock'
import { createCustomerDisplayId } from './customerDisplayId'
import { createCustomerDisplayTabIdentityPresence } from './customerDisplayTabIdentityPresence'

const CUSTOMER_DISPLAY_TAB_ID_CHANNEL_NAME = 'customer_display_tab_identity'
const CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY = 'customerDisplayTabId'
const CUSTOMER_DISPLAY_TAB_ID_LOCK_PREFIX = 'customer-display-tab-id'
const CUSTOMER_DISPLAY_TAB_ID_PROBE_TIMEOUT_MS = 500
const CUSTOMER_DISPLAY_TAB_ID_CLAIM_ATTEMPTS = 3
const CUSTOMER_DISPLAY_TAB_IDENTITY_PROBE = 'tabIdentityProbe'
const CUSTOMER_DISPLAY_TAB_IDENTITY_CLAIM = 'tabIdentityClaim'
const CUSTOMER_DISPLAY_TAB_IDENTITY_MESSAGE_TYPES = new Set([
  CUSTOMER_DISPLAY_TAB_IDENTITY_PROBE,
  CUSTOMER_DISPLAY_TAB_IDENTITY_CLAIM,
])

const customerDisplayTabInstanceId = createCustomerDisplayId()

let currentCustomerDisplayTabId = null
let customerDisplayTabIdInitialization = null
let releaseCustomerDisplayTabIdLock = null
let customerDisplayTabIdentityChannel = null
let pendingCustomerDisplayTabIdClaim = null
let customerDisplayTabIdentityLifecycleRegistered = false

const customerDisplayTabIdentityPresence =
  createCustomerDisplayTabIdentityPresence({
    getTabId: () => currentCustomerDisplayTabId,
    instanceId: customerDisplayTabInstanceId,
  })

const shouldReuseStoredTabId = () => {
  const navigationEntries =
    globalThis.performance?.getEntriesByType?.('navigation')
  const navigationType = navigationEntries?.[0]?.type

  return !navigationType || ['reload', 'back_forward'].includes(navigationType)
}

const getStoredCustomerDisplayTabId = () => {
  try {
    return (
      globalThis.sessionStorage?.getItem(CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY) ||
      null
    )
  } catch (error) {
    console.warn(
      'No se pudo leer la identidad de la pestaña para Customer Display:',
      error
    )
    return null
  }
}

const storeCustomerDisplayTabId = tabId => {
  try {
    globalThis.sessionStorage?.setItem(
      CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY,
      tabId
    )
  } catch (error) {
    console.warn(
      'No se pudo guardar la identidad de la pestaña para Customer Display:',
      error
    )
  }
}

const getCustomerDisplayTabIdLockName = tabId =>
  `${CUSTOMER_DISPLAY_TAB_ID_LOCK_PREFIX}:${tabId}`

const postCustomerDisplayTabIdentityMessage = message => {
  try {
    customerDisplayTabIdentityChannel?.postMessage(message)
    return Boolean(customerDisplayTabIdentityChannel)
  } catch (error) {
    console.warn(
      'No se pudo enviar el mensaje de identidad para Customer Display:',
      error
    )
    return false
  }
}

const postCustomerDisplayTabIdentityClaim = targetInstanceId => {
  postCustomerDisplayTabIdentityMessage({
    type: CUSTOMER_DISPLAY_TAB_IDENTITY_CLAIM,
    tabId:
      currentCustomerDisplayTabId || pendingCustomerDisplayTabIdClaim?.tabId,
    instanceId: customerDisplayTabInstanceId,
    targetInstanceId,
  })
}

const isValidCustomerDisplayTabIdentityMessage = data => {
  if (
    !data ||
    typeof data !== 'object' ||
    !CUSTOMER_DISPLAY_TAB_IDENTITY_MESSAGE_TYPES.has(data.type) ||
    typeof data.tabId !== 'string' ||
    typeof data.instanceId !== 'string'
  ) {
    return false
  }

  return (
    data.type !== CUSTOMER_DISPLAY_TAB_IDENTITY_CLAIM ||
    typeof data.targetInstanceId === 'string'
  )
}

const handleCustomerDisplayTabIdentityMessage = ({ data }) => {
  if (
    !isValidCustomerDisplayTabIdentityMessage(data) ||
    data.instanceId === customerDisplayTabInstanceId
  ) {
    return
  }

  if (data.type === CUSTOMER_DISPLAY_TAB_IDENTITY_PROBE) {
    const claimedTabId =
      currentCustomerDisplayTabId || pendingCustomerDisplayTabIdClaim?.tabId
    const ownsTabId = currentCustomerDisplayTabId === data.tabId
    const winsPendingClaim =
      pendingCustomerDisplayTabIdClaim?.tabId === data.tabId &&
      customerDisplayTabInstanceId < data.instanceId

    if (claimedTabId === data.tabId && (ownsTabId || winsPendingClaim)) {
      postCustomerDisplayTabIdentityClaim(data.instanceId)
    }
    return
  }

  if (
    data.type === CUSTOMER_DISPLAY_TAB_IDENTITY_CLAIM &&
    data.targetInstanceId === customerDisplayTabInstanceId &&
    data.tabId === pendingCustomerDisplayTabIdClaim?.tabId
  ) {
    pendingCustomerDisplayTabIdClaim.resolve(true)
  }
}

const getCustomerDisplayTabIdentityChannel = () => {
  if (customerDisplayTabIdentityChannel) {
    return customerDisplayTabIdentityChannel
  }
  if (typeof globalThis.BroadcastChannel === 'undefined') return null

  try {
    customerDisplayTabIdentityChannel = new globalThis.BroadcastChannel(
      CUSTOMER_DISPLAY_TAB_ID_CHANNEL_NAME
    )
    customerDisplayTabIdentityChannel.onmessage =
      handleCustomerDisplayTabIdentityMessage
  } catch (error) {
    console.warn(
      'No se pudo crear el canal de identidad para Customer Display:',
      error
    )
    customerDisplayTabIdentityChannel = null
  }

  return customerDisplayTabIdentityChannel
}

const isCustomerDisplayTabIdClaimed = tabId => {
  if (customerDisplayTabIdentityPresence.hasForeignPresence(tabId)) {
    return Promise.resolve(true)
  }

  const identityChannel = getCustomerDisplayTabIdentityChannel()
  if (!identityChannel) return Promise.resolve(null)

  return new Promise(resolve => {
    let settled = false

    const finish = isClaimed => {
      if (settled) return

      settled = true
      globalThis.clearTimeout(timeoutId)
      if (pendingCustomerDisplayTabIdClaim?.resolve === finish) {
        pendingCustomerDisplayTabIdClaim = null
      }
      resolve(isClaimed)
    }

    const timeoutId = globalThis.setTimeout(
      () => finish(false),
      CUSTOMER_DISPLAY_TAB_ID_PROBE_TIMEOUT_MS
    )

    pendingCustomerDisplayTabIdClaim = { tabId, resolve: finish }
    if (
      !postCustomerDisplayTabIdentityMessage({
        type: CUSTOMER_DISPLAY_TAB_IDENTITY_PROBE,
        tabId,
        instanceId: customerDisplayTabInstanceId,
      })
    ) {
      finish(null)
    }
  })
}

const initializeCustomerDisplayTabIdWithLock = async storedTabId => {
  let tabId = storedTabId || createCustomerDisplayId()

  for (
    let attempt = 0;
    attempt < CUSTOMER_DISPLAY_TAB_ID_CLAIM_ATTEMPTS;
    attempt += 1
  ) {
    const lockResult = await acquireExclusiveCustomerDisplayLock(
      getCustomerDisplayTabIdLockName(tabId)
    )

    if (lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.acquired) {
      releaseCustomerDisplayTabIdLock = lockResult.release
      return tabId
    }

    if (lockResult.status !== CUSTOMER_DISPLAY_LOCK_STATUS.unavailable) {
      if (lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.failed) {
        console.warn(
          'No se pudo reservar la identidad de la pestaña para Customer Display:',
          lockResult.error || lockResult.reason
        )
      }
      return null
    }

    tabId = createCustomerDisplayId()
  }

  return null
}

const initializeCustomerDisplayTabIdWithChannel = async storedTabId => {
  if (!storedTabId || !shouldReuseStoredTabId()) {
    return createCustomerDisplayId()
  }

  const isClaimed = await isCustomerDisplayTabIdClaimed(storedTabId)

  return isClaimed === false
    ? storedTabId
    : `tab-${customerDisplayTabInstanceId}`
}

const releaseCustomerDisplayTabIdentity = event => {
  if (event?.persisted) return

  customerDisplayTabIdentityPresence.release()
  releaseCustomerDisplayTabIdLock?.()
  releaseCustomerDisplayTabIdLock = null
  pendingCustomerDisplayTabIdClaim = null
  customerDisplayTabIdentityChannel?.close()
  customerDisplayTabIdentityChannel = null
}

const registerCustomerDisplayTabIdentityLifecycle = () => {
  if (customerDisplayTabIdentityLifecycleRegistered) return

  customerDisplayTabIdentityLifecycleRegistered = true
  globalThis.document?.addEventListener?.(
    'visibilitychange',
    customerDisplayTabIdentityPresence.refresh
  )
  globalThis.addEventListener?.(
    'storage',
    customerDisplayTabIdentityPresence.handleStorageChange
  )
  globalThis.addEventListener?.(
    'pageshow',
    customerDisplayTabIdentityPresence.refresh
  )
  globalThis.addEventListener?.('pagehide', releaseCustomerDisplayTabIdentity)
}

export const initializeCustomerDisplayTabId = () => {
  if (customerDisplayTabIdInitialization) {
    return customerDisplayTabIdInitialization
  }

  customerDisplayTabIdInitialization = Promise.resolve().then(async () => {
    const storedTabId = getStoredCustomerDisplayTabId()
    const lockedTabId = await initializeCustomerDisplayTabIdWithLock(
      currentCustomerDisplayTabId || storedTabId
    )

    currentCustomerDisplayTabId =
      lockedTabId ||
      (await initializeCustomerDisplayTabIdWithChannel(storedTabId))
    storeCustomerDisplayTabId(currentCustomerDisplayTabId)
    getCustomerDisplayTabIdentityChannel()
    customerDisplayTabIdentityPresence.start()
    registerCustomerDisplayTabIdentityLifecycle()

    return currentCustomerDisplayTabId
  })

  return customerDisplayTabIdInitialization
}

export const getCustomerDisplayTabId = () => {
  if (currentCustomerDisplayTabId) return currentCustomerDisplayTabId

  const storedTabId = getStoredCustomerDisplayTabId()

  currentCustomerDisplayTabId =
    storedTabId && shouldReuseStoredTabId()
      ? storedTabId
      : createCustomerDisplayId()
  storeCustomerDisplayTabId(currentCustomerDisplayTabId)

  return currentCustomerDisplayTabId
}
